import { expectBoolean, expectArray, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseOccurrenceLifecycleStatus, parseOccurrenceResolutionType } from "./schedule-admin";
import { parseFamilyScheduleDisplayFlags } from "./family";
function expectOptionalNumber(value, path) {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${path} must be a finite number`);
    }
    return value;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function resolveTeacherExpectedParticipantCount(value) {
    return value.expectedParticipantCount ?? value.studentCount ?? null;
}
export function resolveTeacherExpectedStudents(value) {
    return value.expectedStudents ?? [];
}
function parseTeacherExpectedStudentAbsenceStatus(value, path) {
    if (value === "submitted"
        || value === "approved"
        || value === "cancelled"
        || value === "rejected") {
        return value;
    }
    if (value === "none" || value === null || value === undefined) {
        return "none";
    }
    throw new Error(`${path} must be a valid teacher expected student absence status`);
}
export function parseTeacherExpectedStudent(value, path = "teacherExpectedStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        absenceStatus: parseTeacherExpectedStudentAbsenceStatus(record.absenceStatus, `${path}.absenceStatus`),
        isAbsent: record.isAbsent === undefined ? false : expectBoolean(record.isAbsent, `${path}.isAbsent`)
    };
}
export function parseTeacherScheduleItem(value, path = "teacherScheduleItem") {
    const record = expectRecord(value, path);
    const studentCount = expectOptionalNumber(record.studentCount, `${path}.studentCount`);
    const expectedParticipantCount = hasOwn(record, "expectedParticipantCount")
        ? expectOptionalNumber(record.expectedParticipantCount, `${path}.expectedParticipantCount`)
        : studentCount;
    const expectedStudents = hasOwn(record, "expectedStudents")
        ? (record.expectedStudents === null
            ? null
            : expectArray(record.expectedStudents, parseTeacherExpectedStudent, `${path}.expectedStudents`))
        : null;
    return {
        occurrenceId: expectString(record.occurrenceId, `${path}.occurrenceId`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        occurrenceDate: expectString(record.occurrenceDate, `${path}.occurrenceDate`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioNameSnapshot: expectOptionalString(record.studioNameSnapshot, `${path}.studioNameSnapshot`),
        studioDecisionStatus: expectOptionalString(record.studioDecisionStatus, `${path}.studioDecisionStatus`),
        teacherAssignmentStatus: expectOptionalString(record.teacherAssignmentStatus, `${path}.teacherAssignmentStatus`),
        lifecycleStatus: parseOccurrenceLifecycleStatus(record.lifecycleStatus, `${path}.lifecycleStatus`),
        resolutionType: parseOccurrenceResolutionType(record.resolutionType, `${path}.resolutionType`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        operatorNote: expectOptionalString(record.operatorNote, `${path}.operatorNote`),
        teacherNameSnapshots: expectArray(record.teacherNameSnapshots, expectString, `${path}.teacherNameSnapshots`),
        displayFlags: parseFamilyScheduleDisplayFlags(record.displayFlags, `${path}.displayFlags`),
        expectedParticipantCount,
        studentCount,
        expectedStudents
    };
}
export function parseTeacherOccurrenceDetail(value, path = "teacherOccurrenceDetail") {
    return parseTeacherScheduleItem(value, path);
}
export function parseTeacherScheduleMonthGroup(value, path = "teacherScheduleMonthGroup") {
    const record = expectRecord(value, path);
    return {
        yearMonth: expectString(record.yearMonth, `${path}.yearMonth`),
        label: expectString(record.label, `${path}.label`),
        items: expectArray(record.items, parseTeacherScheduleItem, `${path}.items`)
    };
}
export function parseTeacherScheduleResponse(value, path = "teacherScheduleResponse") {
    const record = expectRecord(value, path);
    return {
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        todayDate: expectString(record.todayDate, `${path}.todayDate`),
        rangeStart: expectString(record.rangeStart, `${path}.rangeStart`),
        rangeEnd: expectString(record.rangeEnd, `${path}.rangeEnd`),
        todayItems: expectArray(record.todayItems, parseTeacherScheduleItem, `${path}.todayItems`),
        upcomingItems: expectArray(record.upcomingItems, parseTeacherScheduleItem, `${path}.upcomingItems`),
        monthGroups: expectArray(record.monthGroups, parseTeacherScheduleMonthGroup, `${path}.monthGroups`)
    };
}
